import { getCookie, setCookie } from "@baggie/core";

const RELEWISE_ANONYMOUS_USER_COOKIE = "relewise_anonymous_user_id";
const COOKIE_EXPIRY_DAYS = 365; // 1 year

/**
 * Generate a simple UUID v4
 */
const generateUUID = (): string => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

/**
 * Gets or creates an anonymous user ID for Relewise tracking.
 * Only creates/stores the ID if marketing consent has been given OR in development (localhost).
 *
 * @param hasMarketingConsent - Whether the user has accepted marketing/tracking cookies
 * @returns The anonymous user ID or undefined if no consent
 */
export const getOrCreateAnonymousUserId = (hasMarketingConsent: boolean): string | undefined => {
    const isLocalhost = location.hostname === "localhost" || location.hostname === "127.0.0.1";

    // Allow tracking in development OR if marketing consent is given
    if (!hasMarketingConsent && !isLocalhost) {
        return undefined;
    }

    // Try to get existing cookie
    let anonymousUserId = getCookie(RELEWISE_ANONYMOUS_USER_COOKIE, true);

    // If no cookie exists, create a new one
    if (!anonymousUserId) {
        anonymousUserId = generateUUID();

        // Calculate expiry date
        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + COOKIE_EXPIRY_DAYS);

        // Set the cookie
        setCookie(RELEWISE_ANONYMOUS_USER_COOKIE, anonymousUserId, {
            expires: expiryDate,
            path: "/",
            secure: location.protocol === "https:",
        });
    }

    return anonymousUserId;
};

/**
 * Clears the anonymous user ID cookie.
 * Should be called when user logs in or revokes consent.
 */
export const clearAnonymousUserId = (): void => {
    setCookie(RELEWISE_ANONYMOUS_USER_COOKIE, "", {
        expires: new Date(0),
        path: "/",
    });
};
